import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Stack,
  TextField,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import FileClockOutline from 'mdi-material-ui/FileClockOutline';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  GoogleMap,
  Polyline,
  Marker,
  InfoWindow
} from '@react-google-maps/api';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SpeedIcon from '@mui/icons-material/Speed';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import LocationModal from '../Trips/location-modal.tsx';
import EditTripModal from './edit-trip-modal.tsx';
import { toast } from 'react-toastify';
import { getTrips, editRemarks, getVehicleRoute } from './action.ts';

const mapContainerStyle = { width: '100%', height: '100%' };

const TripsListPage: React.FC<{
  open: boolean;
  onClose: () => void;
  vehicleId: string;
  defaultVehicleNo?: string;
  defaultDriverName?: string;
  defaultDriverPhone?: string;
  onTripSelect?: (startTime: string, endTime: string) => void;
}> = ({ open, onClose, vehicleId, defaultVehicleNo, defaultDriverName, defaultDriverPhone, onTripSelect }) => {

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [fromDate, setFromDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [tripsList, setTripsList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTripForEdit, setSelectedTripForEdit] = useState<any>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationModalData, setLocationModalData] = useState({ start: '', end: '' });
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [routePath, setRoutePath] = useState<{ lat: number; lng: number }[]>([]);
  const [routeStart, setRouteStart] = useState<{ lat: number; lng: number } | null>(null);
  const [routeEnd, setRouteEnd] = useState<{ lat: number; lng: number } | null>(null);
  const [tripStops, setTripStops] = useState<{ lat: number; lng: number; timestamp: string; type?: string }[]>([]);
  const [selectedStop, setSelectedStop] = useState<{ lat: number; lng: number; timestamp: string; index: number; type?: string } | null>(null); 
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [isTripCompleted, setIsTripCompleted] = useState(false);
  const [isMapMaximized, setIsMapMaximized] = useState(false); 
  const pageSize = 10;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatShortDateTime = (dateStr: string | null) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    let hours = d.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const mins = d.getMinutes().toString().padStart(2, '0');
    return `${String(d.getDate()).padStart(2, '0')} ${monthNames[d.getMonth()]}, ${hours}:${mins} ${ampm}`;
  };

  const formatDateTimeFull = (dateStr: string | null) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    
    let hours = d.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const mins = String(d.getMinutes()).padStart(2, '0');
    
    return `${dd}/${mm}/${yyyy} ${hours}:${mins} ${ampm}`;
  };

  const formatAddress = (location: string | null) => {
    if (!location) return "Location not captured";
    const parts = location.split(',').map(p => p.trim()).filter(p => p.length > 0);
    let targetSegment = parts.length >= 3 ? parts[parts.length - 4] : parts[0];
    const words = targetSegment?.split(/\s+/) || [];
    const lastTwo = words.length >= 2 ? words.slice(-2) : words;
    return lastTwo.join(' ');
  };

  const handleFetchTrips = (newPage: number) => {
    if (!vehicleId || (loading && newPage !== 0) || (!hasMore && newPage !== 0)) return;
    setLoading(true);
    getTrips(
      pageSize, newPage, fromDate, toDate,
      (response: any) => {
        setLoading(false);
        let trips = response?.message?.trips_list;
        if (!Array.isArray(trips) && Array.isArray(response?.message)) {
          trips = response.message;
        }
        const safeTrips = Array.isArray(trips) ? trips : [];
        if (safeTrips.length === 0) {
          setHasMore(false);
          if (newPage === 0) setTripsList([]);
        } else {
          if (newPage === 0) {
            setTripsList(safeTrips);
          } else {
            setTripsList(prev => [...prev, ...safeTrips]);
          }
          if (safeTrips.length < pageSize) {
            setHasMore(false);
          }
        }
      },
      (errMessage: any) => {
        setLoading(false);
        toast.error(errMessage);
        if (newPage === 0) setTripsList([]);
      },
      [vehicleId], '', ''
    );
  };

  useEffect(() => {
    if (open) {
      setTripsList([]);
      setPage(0);
      setHasMore(true);
      setSelectedTripId(null);
      setRoutePath([]);
      setTripStops([]);
      setIsMapMaximized(false);
      handleFetchTrips(0);
    }
  }, [open, fromDate, toDate]);

  const handleShowRoute = (trip: any) => {
    setSelectedTripId(trip.id);
    setRouteLoading(true);
    setRoutePath([]);
    setTripStops([]);
    setRouteStart(null);
    setRouteEnd(null);
    setSelectedStop(null);
    setIsTripCompleted(!!(trip.end_lat && trip.end_lng)); 
    setIsMapMaximized(false);
    if (onTripSelect) {
        onTripSelect(trip.start_time, trip.end_time || new Date().toISOString());
    }
    
    const formatForApi = (dateStr: string) => {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    let endTimeToUse = trip.end_time;
    if (!endTimeToUse) {
      const startDate = new Date(trip.start_time);
      startDate.setHours(23, 59, 0, 0);
      endTimeToUse = formatForApi(startDate.toISOString());
    } else {
      endTimeToUse = formatForApi(endTimeToUse);
    }
    const startTimeToUse = formatForApi(trip.start_time);

    getVehicleRoute(
        vehicleId,
        startTimeToUse,
        endTimeToUse,
        (response: any) => {
            setRouteLoading(false);
            const routeData = response?.data || response;
            if (routeData && routeData.polyline) {
                if (window.google && window.google.maps) {
                    try {
                        const decodedPath = window.google.maps.geometry.encoding.decodePath(routeData.polyline);
                        const formattedPath = decodedPath.map((latLng) => ({
                            lat: latLng.lat(),
                            lng: latLng.lng()
                        }));
                        setRoutePath(formattedPath);

                        if (trip.start_lat && trip.start_lng) {
                            setRouteStart({
                                lat: Number(trip.start_lat),
                                lng: Number(trip.start_lng)
                            });
                        } else if (formattedPath.length > 0) {
                            setRouteStart(formattedPath[0]);
                        }

                        if (trip.end_lat && trip.end_lng) {
                            setRouteEnd({
                                lat: Number(trip.end_lat),
                                lng: Number(trip.end_lng)
                            });
                        } else if (routeData?.end?.lat && routeData?.end?.lng) {
                            setRouteEnd({
                                lat: Number(routeData.end.lat),
                                lng: Number(routeData.end.lng)
                            });
                        } else if (formattedPath.length > 0) {
                            setRouteEnd(formattedPath[formattedPath.length - 1]);
                        }

                        const engineStops = (routeData.engine_events || [])
                            .filter((event: any) => event.type === 'stop')
                            .map((event: any) => ({
                                lat: Number(event.lat),
                                lng: Number(event.lng),
                                timestamp: event.timestamp || "",
                                type: "engine"
                            }));

                        const idleStops = (routeData.idle_events || [])
                            .map((event: any) => ({
                                lat: Number(event.start_latitude || event.lat),
                                lng: Number(event.start_longitude || event.lng),
                                timestamp: event.start_time || event.timestamp || "",
                                type: "idle"
                            }));

                        let combinedStops = [...engineStops, ...idleStops];
                        const filteredStops: any[] = [];
                        combinedStops.forEach((current) => {
                            const p1 = new window.google.maps.LatLng(current.lat, current.lng);
                            const isTooClose = filteredStops.some((kept) => {
                                const p2 = new window.google.maps.LatLng(kept.lat, kept.lng);
                                const dist = window.google.maps.geometry.spherical.computeDistanceBetween(p1, p2);
                                return dist < 100;
                            });
                            if (!isTooClose) {
                                filteredStops.push(current);
                            }
                        });
                        combinedStops = filteredStops;
                        
                        setTripStops(combinedStops);

                        if (mapInstance) {
                            const bounds = new window.google.maps.LatLngBounds();
                            formattedPath.forEach(p => bounds.extend(p));
                            combinedStops.forEach((stop: any) => bounds.extend({ lat: stop.lat, lng: stop.lng }));
                            mapInstance.fitBounds(bounds);
                        }
                    } catch (e) {
                        toast.error("Error parsing route data");
                    }
                }
            } else {
                toast.info("No detailed route path available");
            }
        },
        (error: any) => {
            setRouteLoading(false);
            toast.error("Failed to fetch route");
        }
    );
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (!loading && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        handleFetchTrips(nextPage);
      }
    }
  };

  const handleLocationClick = (e: React.MouseEvent, trip: any) => {
    e.stopPropagation();
    setLocationModalData({
      start: trip.start_location || '',
      end: trip.end_location || ''
    });
    setShowLocationModal(true);
  };

  const columns: GridColDef[] = [
    { field: 'index', headerName: 'Sl No', width: 60, valueGetter: (params, row) => tripsList.indexOf(row) + 1 },
    { 
      field: 'start_time', 
      headerName: 'Start Time', 
      width: 250,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2">{formatDateTimeFull(params.row.start_time)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span" sx={{ color: 'green', fontSize: '18px' }}>•</Box> {formatAddress(params.row.start_location)}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'end_time', 
      headerName: 'End Time', 
      width: 250,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2">{params.row.end_time ? formatDateTimeFull(params.row.end_time) : 'In Progress'}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box component="span" sx={{ color: 'red', fontSize: '18px' }}>•</Box> {params.row.end_location ? formatAddress(params.row.end_location) : '--'}
          </Typography>
        </Box>
      )
    },
    { field: 'odometer', headerName: 'Odometer Start/End', width: 180, valueGetter: (p, row) => `${row.odometer_start || 0} / ${row.odometer_end || 0}` },
    { field: 'gps_kms', headerName: 'GPS KM', flex:1 },
    { field: 'orders', headerName: 'Orders Delivered/Picked', width: 180, valueGetter: (p, row) => `${row.orders_delivered || 0}/${row.orders_picked || 0}` },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 210,
      renderCell: (params) => (
        <Stack direction="row">
          <IconButton size="small" onClick={(e) => handleEditClick(e, params.row)} color="primary"><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" onClick={(e) => handleLocationClick(e, params.row)} color="error"><LocationOnIcon fontSize="small" /></IconButton>
          <IconButton size="small" onClick={(e) =>  handleShowRoute(params.row)} color="primary"><FileClockOutline fontSize="small" /></IconButton>
        </Stack>
      )
    }
  ];

  const handleEditClick = (e: React.MouseEvent, trip: any) => {
    e.stopPropagation();
    setSelectedTripForEdit(trip);
    setShowEditModal(true);
  };

  const handleSaveTrip = (payload: any) => {
    if (!selectedTripForEdit) return;
    const tripId = selectedTripForEdit.id;
    const changedParams: Record<string, string> = {};

    if (payload.odometer_start !== selectedTripForEdit.odometer_start) changedParams.odometer_start = payload.odometer_start.toString();
    if (payload.odometer_end !== selectedTripForEdit.odometer_end) changedParams.odometer_end = payload.odometer_end.toString();
    if (payload.orders_picked !== selectedTripForEdit.orders_picked) changedParams.orders_picked = payload.orders_picked.toString();
    if (payload.orders_delivered !== selectedTripForEdit.orders_delivered) changedParams.orders_delivered = payload.orders_delivered.toString();

    if (Object.keys(changedParams).length === 0) {
      toast.info('No changes to save');
      return;
    }

    editRemarks(
      tripId,
      changedParams,
      () => {
        toast.success('Trip updated successfully');
        setShowEditModal(false);
        setSelectedTripForEdit(null);
        setPage(0);
        setHasMore(true);
        handleFetchTrips(0);
      },
      (err: any) => {
        toast.error(err || 'Failed to update trip.');
      }
    );
  };

  const handleCloseMap = (e?: React.MouseEvent) => {
    if(e) e.stopPropagation();
    setSelectedTripId(null);
    setIsMapMaximized(false);
  }

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'relative', 
        zIndex: 100, 
        bgcolor: '#f5f5f5',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: '85vh',
        overflow: 'hidden',
        marginTop: isDesktop ?'65px' : 0
      }}
    >
      <Paper elevation={1} sx={{ padding: '12px 16px', borderRadius: 0, flexShrink: 0, zIndex: 10, position: 'relative' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton 
              onClick={onClose} 
              sx={{ 
                  zIndex: 20, 
                  background: 'linear-gradient(45deg, black, #ed1b1b)',
                  color: 'white', 
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                  width: 40,
                  height: 40,
                  marginRight: 1, 
                  '&:hover': { background: 'linear-gradient(45deg, #333, #ff3b3b)' }
              }}
            >
              <ArrowBackIcon sx={{ color: '#ffffff' }} />
            </IconButton>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Typography variant="h6" fontWeight="bold" color="#1a73e8" sx={{ lineHeight: 1.2 }}>
                {defaultVehicleNo || "--"}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                - {defaultDriverName || "Unknown"}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="From" type="date" size="small"
              value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white', width: 160 }}
            />
            <TextField
              label="To" type="date" size="small"
              value={toDate} onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white', width: 160 }}
            />
          </Stack>
        </Box>
      </Paper>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: isDesktop ? 'row-reverse' : 'column', 
        flexGrow: 1, 
        overflow: 'hidden', 
        transition: 'all 0.3s ease' 
      }}>
        
        {(selectedTripId) && (
            <Box 
              sx={{ 
                height: isDesktop ? '100%' : (isMapMaximized ? '100%' : '30%'), 
                width: isDesktop ? '45%' : '100%', 
                position: 'relative', 
                borderLeft: isDesktop ? '1px solid #e0e0e0' : 'none', 
                borderBottom: isDesktop ? 'none' : '2px solid #1a73e8', 
                flexShrink: 0,
                transition: 'height 0.3s ease'
              }}
            >
                {routeLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', bgcolor: '#f0f0f0' }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Loading Route...</Typography>
                    </Box>
                ) : (
                     <GoogleMap
                     mapContainerStyle={mapContainerStyle}
                     zoom={12}
                     center={routeStart || { lat: 0, lng: 0 }}
                     onLoad={setMapInstance}
                     options={{ disableDefaultUI: true, zoomControl: true, mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}
                   >
                     {routePath.length > 0 && <Polyline path={routePath} options={{ strokeColor: '#1a73e8', strokeWeight: 5 }} />}
                     {routeStart &&   <Marker
                             key="start-point"
                             position={routeStart}
                             icon={{
                                 path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                 scale: 8,
                                 fillColor: "#1a73e8", 
                                 fillOpacity: 1,
                                 strokeWeight: 2,
                                 strokeColor: "#FFFFFF", 
                                 rotation: 0
                             }}
                         />}
                     {routeEnd && <Marker position={routeEnd} icon={{ url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png" }} />}
                     
                     {isDesktop && tripStops.map((stop, index) => (
                        <Marker
                            key={`stop-${index}`}
                            position={{ lat: stop.lat, lng: stop.lng }}
                            onClick={() => setSelectedStop({ ...stop, index })}
                            label={{ text: (index + 1).toString(), color: "white", fontSize: "12px", fontWeight: "bold" }}
                            icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 10, 
                                fillColor: stop.type === "engine" ? "#d32f2f" : "#f18d12",
                                fillOpacity: 1, 
                                strokeWeight: 2, 
                                strokeColor: "white",
                            }}
                        />
                     ))}

                     {isDesktop && selectedStop && (
                        <InfoWindow
                            position={{ lat: selectedStop.lat, lng: selectedStop.lng }}
                            onCloseClick={() => setSelectedStop(null)}
                            options={{ pixelOffset: new window.google.maps.Size(0, -5) }}
                        >
                             <Box sx={{ p: 0.5 }}>
                                <Typography variant="subtitle2" fontWeight="bold">Stop #{selectedStop.index + 1} ({selectedStop.type === 'engine' ? 'Engine Off' : 'Idle'})</Typography>
                                <Typography variant="caption" display="block">
                                    {new Date(selectedStop.timestamp).toLocaleString()}
                                </Typography>
                            </Box>
                        </InfoWindow>
                     )}
                   </GoogleMap>
                )}

                <IconButton 
                    onClick={handleCloseMap}
                    size="small"
                    sx={{ position: 'absolute', top: 10, left: 10, bgcolor: 'white', '&:hover':{bgcolor:'#f5f5f5'}, boxShadow: 2, zIndex: 5 }}
                >
                    {isDesktop ? <CloseIcon /> : <CloseIcon sx={{ transform: 'rotate(180deg)' }} />}
                </IconButton>

                {!isDesktop && (
                  <IconButton 
                      onClick={(e) => { e.stopPropagation(); setIsMapMaximized(!isMapMaximized); }}
                      size="small"
                      sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'white', '&:hover':{bgcolor:'#f5f5f5'}, boxShadow: 2, zIndex: 5 }}
                  >
                      {isMapMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  </IconButton>
                )}
            </Box>
        )}
        <Box 
            onScroll={handleScroll} 
            sx={{ 
                display: (!isDesktop && selectedTripId && isMapMaximized) ? 'none' : 'block',
                width: isDesktop ? (selectedTripId ? '55%' : '100%') : '100%', 
                height: (!isDesktop && selectedTripId) ? (isMapMaximized ? '0%' : '70%') : '100%',
                overflowY: 'auto', 
                padding: isDesktop ? '16px' : '10px', 
                background: isDesktop ? '#f4f6f8' : 'linear-gradient(135deg,#000 20%,#e63946)',
                transition: 'width 0.3s ease'
            }}
        >
          {isDesktop ? (
            <Box className="table-container custom-data-grid" sx={{ marginLeft:'0px'}}>
              <DataGrid
                rows={tripsList}
                columns={columns}
                loading={loading}
                getRowId={(row) => row.id}
                onRowClick={(p) => handleShowRoute(p.row)}
                rowHeight={70}
                disableRowSelectionOnClick
                disableColumnResize
                hideFooterPagination
                classes={{ columnHeader: 'red-header' }}
              />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: selectedTripId ? 0 : 3, overflowX: 'auto' }}>
              <Table size="small" aria-label="mobile trips table" sx={{ minWidth: 320 }}>
                <TableHead sx={{ bgcolor: '#f4f6f8' }}>
                  <TableRow>
                    <TableCell sx={{ p: 1, fontSize: '0.75rem', fontWeight: 'bold', width: '20px' }}>#</TableCell>
                    <TableCell sx={{ p: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>Trip Details</TableCell>
                    <TableCell sx={{ p: 1, fontSize: '0.75rem', fontWeight: 'bold' }}>Stats</TableCell>
                    <TableCell sx={{ p: 1, fontSize: '0.75rem', fontWeight: 'bold', align: 'center', width: '40px' }}>Act</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tripsList?.map((trip, index) => (
                    <TableRow
                      key={trip.id}
                      onClick={() => handleShowRoute(trip)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: selectedTripId === trip.id ? '#e3f2fd' : 'white',
                        borderBottom: selectedTripId === trip.id ? '2px solid #1a73e8' : 'none',
                        '&:last-child td, &:last-child th': { border: 0 },
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <TableCell sx={{ p: 1, fontSize: '0.8rem', verticalAlign: 'top' }}>{index + 1}</TableCell>
                      
                      <TableCell sx={{ p: 1, verticalAlign: 'top' }}>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Box>
                            <Typography variant="caption" fontWeight="bold" color="success.main" display="flex" alignItems="center" gap={0.5}>
                              <Box component="span" sx={{ fontSize: '10px' }}>🟢</Box> {formatShortDateTime(trip.start_time)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="flex" alignItems="flex-start" gap={0.5} sx={{ pl: 1.5, lineHeight: 1.1 }}>
                              {formatAddress(trip.start_location)}
                            </Typography>
                          </Box>
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" fontWeight="bold" color="error.main" display="flex" alignItems="center" gap={0.5}>
                              <Box component="span" sx={{ fontSize: '10px' }}>🔴</Box> {trip.end_time ? formatShortDateTime(trip.end_time) : 'In Progress'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="flex" alignItems="flex-start" gap={0.5} sx={{ pl: 1.5, lineHeight: 1.1 }}>
                              {trip.end_location ? formatAddress(trip.end_location) : '--'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell sx={{ p: 1, verticalAlign: 'top' }}>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Typography variant="caption" display="flex" alignItems="center" gap={0.5} sx={{ whiteSpace: 'nowrap' }}>
                            <SpeedIcon sx={{ fontSize: 14, color: '#757575' }} /> 
                            {trip.odometer_start || 0} - {trip.odometer_end || 0}
                          </Typography>
                          <Typography variant="caption" display="flex" alignItems="center" gap={0.5} sx={{ whiteSpace: 'nowrap' }}>
                            <DirectionsCarIcon sx={{ fontSize: 14, color: '#757575' }} /> 
                            {trip.gps_kms ? `${trip.gps_kms} km` : '--'}
                          </Typography>
                          <Typography variant="caption" display="flex" alignItems="center" gap={0.5} sx={{ whiteSpace: 'nowrap' }}>
                            <ShoppingCartIcon sx={{ fontSize: 14, color: '#757575' }} /> 
                            {trip.orders_delivered || 0}/{trip.orders_picked || 0}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell sx={{ p: 1, align: 'center', verticalAlign: 'top' }}>
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleEditClick(e, trip)} 
                          sx={{ color: '#1976d2', padding: 0.5, bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}
                        >
                          <EditIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {loading && (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} sx={{color: isDesktop ? 'primary.main' : 'white'}} />
            </Box>
          )}
         
        </Box>
      </Box>

      <EditTripModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        trip={selectedTripForEdit}
        onSave={handleSaveTrip}
      />

      <LocationModal
        open={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        startLocation={locationModalData.start}
        endLocation={locationModalData.end}
      />
    </Box>
  );
};

export default TripsListPage;